import { CommonEnums } from 'dashboard-services/api/common';
import { DataStorageEnums, DataStorageQuery, GetDataBody, Key } from 'dashboard-services/api/dataStorage';
import Query from 'dashboard-services/api/Query';
import TimeService from 'dashboard-services/services/TimeService';

import getApiConfig from 'api/ApiConfig';
import FORMULAS from 'resources/constants/Formulas.js';
import properties from 'resources/constants/properties.json';

import ExcelUtils from './ExcelUtils';
import FormulaUtils from './FormulaUtils';
import { normalizeFormulaArgument, Parameters, splitFormulaArguments } from './ParameterBuilder';
import { throttledFetchNoMillisLimit } from './ThrottleUtils';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import Papa from 'papaparse';

export default(() => {
  const TS_PARAMS_PREFIX = "TS Parameters: "
  const DAILY_DATE_FORMAT = "yyyy-MM-dd"
  const DATE_HEADER = "Date"

  const PLAIN_PARSED_TS_PARAMS = ["timeZone", "dateFormat", "range", "lastType", "lastTypeAmount", "order", "formatType", "csvHeaders", "shouldTranspose", "sortKeys", "sortKeysBy", "sortKeysOrder",]
  const SUPPORTED_TS_PARAMS = PLAIN_PARSED_TS_PARAMS.concat(["startDate", "endDate", "keys", "groupName", "columns", "symbols", "metadatas", "pattern", "query"])

  const csvHeadersOptions = [{
    label: "Symbol",
    value: DataStorageEnums.CSV_HEADER_STATICS.SYMBOL_VALUES,
    type: DataStorageEnums.CSV_HEADER_TYPE.DEFAULT
  }, {
    label: "Column",
    value: DataStorageEnums.CSV_HEADER_STATICS.SYMBOL_COLUMN,
    type: DataStorageEnums.CSV_HEADER_TYPE.DEFAULT
  }, {
    label: "Group",
    value: DataStorageEnums.CSV_HEADER_STATICS.GROUP_NAME,
    type: DataStorageEnums.CSV_HEADER_TYPE.DEFAULT
  }]

  const DEFAULT_CSV_HEADERS = [{
    name: "Description",
    type: DataStorageEnums.CSV_HEADER_TYPE.METADATA_KEY
  }, {
    name: DataStorageEnums.CSV_HEADER_STATICS.SYMBOL_VALUES,
    type: DataStorageEnums.CSV_HEADER_TYPE.DEFAULT
  }, {
    name: DataStorageEnums.CSV_HEADER_STATICS.SYMBOL_COLUMN,
    type: DataStorageEnums.CSV_HEADER_TYPE.DEFAULT
  }]

  const normalizeDateParam = ({ value, isEndDate = false }) => {
    if(FormulaUtils.isLikelyExcelDateSerial(Number(value))) {
      const toReturn = FormulaUtils.excelSerialToDateString({ value, withTime: true, isEndDate });
      return toReturn
    }
    return value
  }

  const getBody = ({ keys, timeZone, range, startDate, endDate, lastType, lastTypeAmount = 500, dateFormat = DAILY_DATE_FORMAT, formatType = DataStorageEnums.DATA_FORMAT.NCSV, csvHeaders = [], sortKeys = "false", sortKeysBy, sortKeysOrder, pattern = "true" } = {}) => {
    let builder = new GetDataBody.Builder()
    if(keys?.length === 1 && Object.keys(keys[0]?.symbols || {}).length === 0 && Object.keys(keys[0]?.metadatas || {}).length === 0 && keys[0]?.groupName && !keys[0]?.query) {
      builder = builder.withGroupName(keys[0]?.groupName)
    } else {
      keys.forEach(({ columns, symbols, groupName, metadatas, query }) => {
        const key = new Key.Builder()
          .withColumns(String(columns) === "*" ? undefined : columns.split(","))
          .withSymbols(symbols)
          .withMetadatas(metadatas)
          .withGroupName(groupName)
          .withQuery(query)
          .withPattern(pattern === "true")
          .withPatternExactMatch(false)
          .withSymbolValuesExactMatch(true)
        builder = builder.withKey(key.build().serialize())
      })
    }
    if(range === DataStorageEnums.DATA_RANGE.BETWEEN) {
      builder = builder
              .withStartDate(startDate === "undefined" || startDate === "Invalid Date" ? undefined : normalizeDateParam({ value: startDate }))
              .withEndDate(endDate === "undefined" || endDate === "Invalid Date" ? undefined : normalizeDateParam({ value: endDate, isEndDate: true }))
    }
    if(range === DataStorageEnums.DATA_RANGE.LAST || range === DataStorageEnums.DATA_RANGE.NEXT) {
      builder = builder
              .withLastType(lastType)
              .withLastTypeAmount(lastTypeAmount)
    }
    if(typeof csvHeaders === "object" && csvHeaders?.length > 0) {
      csvHeaders?.forEach(csvHeader => {
        if(csvHeader?.name) {
          builder = builder.withCsvHeader(csvHeader?.name, csvHeader?.type)
        }
      })
    } else {
      DEFAULT_CSV_HEADERS.forEach(defaultCsvHeader => {
        builder = builder.withCsvHeader(defaultCsvHeader?.name, defaultCsvHeader?.type)
      })
    }
    if(!!keys.findIndex && keys.findIndex(k => Object.keys(k.metadatas || {}).length > 0) > -1) {
      builder = builder.withMetadata(true)
    }

    if(sortKeys === "true" && typeof sortKeysBy === "object") {
      builder = builder.withCsvHeaderSort(sortKeysBy?.key, sortKeysBy?.type, Object.values(CommonEnums.SORT_ORDER).includes(sortKeysOrder) ? sortKeysOrder : CommonEnums.SORT_ORDER.ASCENDING)
    }
    const body = builder
      .withRange(range)
      .withDateFormat(dateFormat)
      .withTimeZone(timeZone)
      .withFormatType(formatType)
      .build()
      .serialize()
    return body
  }

  const getSource = ({ body, provider }) => {
    if(!provider) {
      if(body?.source) {
        return body.source
      }
  
      if(properties.warehouse_hybrid === "true") {
        return "WarehouseService"
      }
    }
    return null
  }

  const getData = async ({ range, order = CommonEnums.SORT_ORDER.DESCENDING, keys, sortSymbols, symbolSize, uniqueProviders, isSilent, ...props } = {}) => async dispatch => {
    const body = getBody({ range: range ? String(range).toUpperCase() : DataStorageEnums.DATA_RANGE.LAST, keys, ...props })
    const bodyLoc = {
      ...body,
      source: getSource({ body, provider: uniqueProviders[0] }),
    }
    let apiBuilder = new Query.Builder(dispatch(getApiConfig()))
      .post(bodyLoc)
      .withOnParse(response => Promise.resolve(response))
      .withFunctionName("tsStream")
      .withQueryParameter("size", -1)
      .withQueryParameter("order", order)
      .withHeader("Target", uniqueProviders[0], true)

    if(sortSymbols !== undefined) {
      apiBuilder = apiBuilder.withQueryParameter("sortSymbols", sortSymbols)
    }
    if(symbolSize !== undefined) {
      apiBuilder = apiBuilder.withQueryParameter("symbolSize", symbolSize)
    }

    if(isSilent !== undefined) {
      apiBuilder = apiBuilder.withShouldDisplayError(false).withFetching(false)
    }

    const rawResponse = await apiBuilder
      .withHeader('Content-Type', 'application/json')
      .withHeader('Accept', '*/*')
      .withHeader('Expect', 'text/csv')
      .build()
      .call()

    const textResponse = await rawResponse.text()
    if(!textResponse) {
      return textResponse;
    }
    const parsedResponse = 
        await new Promise((resolve, reject) =>
          Papa.parse(textResponse, {
            complete: result => {
              const newResult = result.data
              if(newResult[newResult.length - 1]?.length !== newResult[0]?.length) {
                newResult.pop()
              }
              return resolve(newResult)
            },
            error: err => {
              reject(err)
              console.error(String(err))
            }
          })
        )
    return parsedResponse;
  }

  const getDatapoint = ({ parsedParameters } = {}) => async dispatch => {
    dayjs.extend(utc)
    const body = {
      date: dayjs.utc(parsedParameters.date).format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE),
      symbol: parsedParameters.symbol,
      groupName: parsedParameters.groupName,
      column: parsedParameters.column,
      source: parsedParameters.source
    }
    const getQueryBuilder = () => new Query.Builder(dispatch(getApiConfig({ fetchOverride: throttledFetchNoMillisLimit })))
    const textResponse = await DataStorageQuery.QUERIES
      .getDataPoint(getQueryBuilder, { body })
      .mutationFn()
    if (textResponse) {
      return textResponse
    }
  }

  const isDateHeader = header => typeof header === "string" && header.trim().toLowerCase().startsWith(DATE_HEADER.toLowerCase())
  const getFirstAppearingDate = response => {
    if(typeof response === "string") {
      const [headerLine, firstRow] = response.trim().split(/\r?\n/)
      if(!headerLine || !firstRow) {
        return undefined
      }
      const headers = headerLine.split(",")
      const values = firstRow.split(",")
      const dateIndex = headers.findIndex(isDateHeader)
      return dateIndex > -1 ? values[dateIndex] : undefined
    }
  
    if(Array.isArray(response)) {
      const [headers = [], firstRow = []] = response
      const dateIndex = headers.findIndex(isDateHeader)
      return dateIndex > -1 ? firstRow[dateIndex] : undefined
    }
    return response?.values?.[0]?.time
  }

  const isTsFormula = formula => formula?.includes(FORMULAS.LOAD_TS) || formula?.includes(FORMULAS.LOAD_TS.replaceAll(".", "_"))

  const extractParamsFromFormula = formula => {
    const argsString = formula.slice(formula.indexOf("(") + 1, formula.lastIndexOf(")")).replace(/\\"/g, '"')
    const rawParams = splitFormulaArguments(argsString)
    const params = rawParams.map(normalizeFormulaArgument)

    Object.defineProperty(params, "rawParams", {
      value: rawParams,
      enumerable: false,
      configurable: true,
      writable: true
    })
    return params
  }
  const extractParamsFromDatapointFormula = formula => {
    const argsString = formula.slice(formula.indexOf("(") + 1, formula.lastIndexOf(")"))
    const params = []
    let current = ""
    let inQuotes = false
    const pushCurrent = () => {
      params.push(current.trim())
      current = ""
    }
    for (let i = 0; i < argsString.length; i++) {
      const char = argsString[i]
      const nextChar = argsString[i + 1]
      const isQuote = char === '"'
      const isEscapedQoute = isQuote && inQuotes && nextChar === '"'
      const isSeparator = char === "," && !inQuotes

      if (isEscapedQoute) {
        current += '"'
        i++ //skip next qoute
      } else if (isQuote) {
        inQuotes = !inQuotes
      } else if (isSeparator) {
        pushCurrent()
      } else {
        current += char
      }
    }
    if (current.length > 0) {
      pushCurrent()
    }
    return params
  }


  const validateTable = async ({ item, cellValue, cell, formula, returnItem = false }) => { 
    console.info("Validating formula:", formula)
    if((!cellValue || String(cellValue).startsWith(TS_PARAMS_PREFIX)) && isTsFormula(formula)) {
      const parsedFormula = await ExcelUtils.parseFormula({
        formula,
        address: cell.address,
        formulaType: FORMULAS.LOAD_TS,
        extractParamsFromFormula
      })
      const parsedParameters = new Parameters.Builder()
        .withUserParameters(parsedFormula)
        .withGrouping(["groupName", "columns", "symbols", "metadatas", "pattern", "query", "source"])
        .build();
        // .withGroupingWithIndex(["offsetType", "offsetAmount", "fillType"]) if we need key params
      const toReturn = {
        formula,
        parsedParameters,
        prefix: TS_PARAMS_PREFIX,
        address: cell.address
      }
      if(returnItem) {
        return toReturn;
      }
      return item.concat(toReturn)
    }
    return item
  }

  const paramsToFormula = params => {
    const groupedRawParams = params.getGroupedRawParams?.() || []
    const groupedParams = params.getGroupedParams().map((item, index) => Object.entries(item).map(([k, v]) => (
      Object.hasOwn(groupedRawParams?.[index] || {}, k) ? groupedRawParams[index][k] : `"${k}=${v}"`
    )).join(", ")).join(", ")
    const regularRawParams = params.getRawParameters?.() || {}
    const regularParams = Object.entries(params.getParameters()).map(([k, v]) => (
      Object.hasOwn(regularRawParams, k) ? regularRawParams[k] : `"${k}=${v}"`
    )).join(", ")
    const parts = [groupedParams, regularParams].filter(Boolean).join(",")

    return `=${FORMULAS.LOAD_TS}(${parts})`
  }

  return {
    getData,
    getDatapoint,
    getFirstAppearingDate,
    TS_PARAMS_PREFIX,
    validateTable,
    SUPPORTED_TS_PARAMS,
    PLAIN_PARSED_TS_PARAMS,
    csvHeadersOptions,
    paramsToFormula,
    DEFAULT_CSV_HEADERS,
    isTsFormula,
    DAILY_DATE_FORMAT,
    extractParamsFromFormula,
    extractParamsFromDatapointFormula,
    getBody
  }
})()
